import { useEffect, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { LoadingState } from '../components/LoadingState';
import { useAdminSession } from './useAdminSession';

type RequireAdminProps = {
  children: ReactNode;
};

/**
 * Mirrors `src/auth/RequireAuth.tsx`, minus `ownerOnly`: there is only one
 * admin role. Unauthenticated -> /admin/entrar; mustChangePassword -> forced
 * to /admin/cambiar-contrasena until resolved.
 */
export function RequireAdmin({ children }: RequireAdminProps) {
  const { status, session } = useAdminSession();
  const navigate = useNavigate();
  const mustChangePassword = status === 'authenticated' && Boolean(session?.mustChangePassword);

  useEffect(() => {
    if (status === 'unauthenticated') {
      void navigate({ to: '/admin/entrar', replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (mustChangePassword) {
      void navigate({ to: '/admin/cambiar-contrasena', replace: true });
    }
  }, [mustChangePassword, navigate]);

  if (status === 'loading') return <LoadingState label="Cargando…" />;
  if (status !== 'authenticated') return null;
  if (mustChangePassword) return null;

  return <>{children}</>;
}

type RequireAdminMustChangePasswordProps = {
  children: ReactNode;
};

/**
 * Guards /admin/cambiar-contrasena itself: reachable only while
 * session.mustChangePassword is true, mirroring RequireMustChangePassword.
 */
export function RequireAdminMustChangePassword({ children }: RequireAdminMustChangePasswordProps) {
  const { status, session } = useAdminSession();
  const navigate = useNavigate();
  const allowed = status === 'authenticated' && Boolean(session?.mustChangePassword);

  useEffect(() => {
    if (status === 'unauthenticated') {
      void navigate({ to: '/admin/entrar', replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (status === 'authenticated' && !allowed) {
      void navigate({ to: '/admin', replace: true });
    }
  }, [status, allowed, navigate]);

  if (status === 'loading') return <LoadingState label="Cargando…" />;
  if (status !== 'authenticated') return null;
  if (!allowed) return null;

  return <>{children}</>;
}
